import React, { useEffect } from 'react'
import CloseIcon from '../../assets/icons/close-icon2.svg'
import Button from '../Button'
import BackCaret from '../../assets/icons/left-arrow.svg'
import styled, { css } from 'styled-components'
import { up } from 'styled-breakpoints'
import { hexToRgba, pxToRem } from '../../utils'
import * as PropTypes from 'prop-types'
import { H5 } from '../Typo'

const MODE_NORMAL = 'normal'
const MODE_PROMPT = 'prompt'
const MODE_ALERT = 'alert'

const StyledButton = styled(Button)`
  &.bank-linking-modal,
  &.otp-verification-modal,
  &.successful-modal {
    width: 100%;
    ${up('md')} {
      width: auto;
    }
  }
`

export const ModalFooterWithConfirmation = ({ onClose, onDone, ...props }) => {
  return (
    <div>
      {props.shouldShowCancel && (
        <Button default onClick={onClose}>
          Cancel
        </Button>
      )}

      <StyledButton
        className={props.className}
        primary
        loading={props.loader}
        disabled={props.disable}
        onClick={onDone}
      >
        {props.confirmMessage || 'Confirm'}
      </StyledButton>
    </div>
  )
}

const alertModalFooter = (onClose, alertButtonText) => (
  <Footer>
    <AlertModalButtonWrapper>
      <Button primary onClick={onClose}>
        {alertButtonText}
      </Button>
    </AlertModalButtonWrapper>
  </Footer>
)

const Modal = ({
  HeaderComponent,
  FooterComponent,
  children,
  onClose,
  id,
  withBackButton,
  mode,
  alertButtonText = 'Ok',
  showHeader,
  className,
  wrapperClassname,
  contentClassname,
  bodyClassname
}) => {
  const ExitButton = withBackButton ? Back : Close

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => (document.body.style.overflow = 'auto')
  }, [])

  const isAlertModal = mode === MODE_ALERT
  const isPromptModal = mode === MODE_PROMPT

  const CONFIG = {
    shouldBeLiftedUp: isAlertModal || isPromptModal,
    isSmaller: isAlertModal || isPromptModal,
    hideCloseIcon: !isAlertModal || withBackButton,
    withSpacingAround: isAlertModal && !withBackButton
  }

  return (
    <Wrapper data-testid={id} shouldBeLiftedUp={CONFIG.shouldBeLiftedUp} className={`${className} ${wrapperClassname}`}>
      <Content className={`modal-content ${className} ${contentClassname}`} isSmaller={CONFIG.isSmaller}>
        {showHeader && (
          <Header className={className} withBackButton={withBackButton} withSpacingAround={CONFIG.withSpacingAround}>
            {HeaderComponent}
            <StyledCloseIcon className={className}>
              {CONFIG.hideCloseIcon && <ExitButton data-testid={`${id}-close`} onClick={onClose} />}
            </StyledCloseIcon>
          </Header>
        )}
        <Body className={`modal-body ${className}-body ${bodyClassname}`}>{children}</Body>
        {FooterComponent && <Footer>{FooterComponent}</Footer>}
        {isAlertModal && alertModalFooter(onClose, alertButtonText)}
      </Content>
    </Wrapper>
  )
}

Modal.defaultProps = {
  withBackButton: false,
  showHeader: true,
  mode: MODE_NORMAL.replace,
  loader: false
}

Modal.propTypes = {
  id: PropTypes.string.isRequired,
  HeaderComponent: PropTypes.node,
  FooterComponent: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  withBackButton: PropTypes.bool,
  mode: PropTypes.oneOf([MODE_NORMAL, MODE_PROMPT, MODE_ALERT]),
  showHeader: PropTypes.bool,
  classname: PropTypes.string,
  wrapperClassname: PropTypes.string,
  contentClassname: PropTypes.string,
  bodyClassname: PropTypes.string
}

ModalFooterWithConfirmation.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  confirmMessage: PropTypes.string,
  shouldShowCancel: PropTypes.bool
}

export default Modal

const StyledCloseIcon = styled.article`
  display: inline;
  position: absolute;
  right: 0;
  &.successful-modal {
    display: none;
  }
`

const AlertModalButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  ${up('md')} {
    flex-direction: row;
    justify-content: flex-end;
  }
`

const basePadding = css`
  padding: ${pxToRem(20)};

  ${up('md')} {
    padding: ${pxToRem(28)};
  }
`

const Wrapper = styled.div`
  background-color: ${props => hexToRgba(props.theme.palette.black, 0.4)};
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  position: fixed;
  z-index: ${props => (props.shouldBeLiftedUp ? '1070' : '1060')};
  /* border-radius: 12px; */
  &.nominee-modal {
    overflow: auto;
    padding-bottom: ${pxToRem(32)};
  }
`

const getWarningModalStyles = props => {
  return (
    props.isSmaller &&
    css`
      max-width: 90%;
      max-height: 80%;
      height: auto;
      top: 50%;
      transform: translateY(-50%);
      border-radius: 12px;

      ${up('md')} {
        min-height: auto;
        max-width: ${props => props.theme.layout.container.sm}px;
        min-width: ${props => props.theme.layout.container.sm}px;
      }
    `
  )
}

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  z-index: 1070;

  background-color: ${props => props.theme.palette.white};
  box-shadow: 0 2px 20px 0 ${props => props.theme.colors.boxShadowColor};
  margin: 10% 5%;
  height: auto;
  border-radius: 0.5em;
  opacity: 100%;
  top: 7%;
  ${up('md')} {
    max-height: 80%;
    top: 34%;
    transform: translateY(-50%);
    border-radius: 12px;
    max-width: ${props => props.theme.layout.container.md}px;
  }

  ${up('lg')} {
    top: 34%;
    left: 14%;
    width: 90%;
    min-width: 50%;
    max-height: 70%;
    border-radius: 12px;
    margin: 5% 10%;
  }

  &.bank-linking-modal {
    max-width: 376px;
    min-width: 0;
    max-height: 85%;
    top: 20%;
    ${up('md')} {
      top: 35%;
      left: 30%;
      right: 25%;
    }
  }

  &.notification-modal {
    max-width: 90%;
    min-width: 0;
    max-height: 85%;
    top: 16%;
    ${up('md')} {
      width: 100%;
      max-height: 80%;
      top: 38%;
      transform: translateY(-50%);
      border-radius: 12px;
      max-width: ${props => props.theme.layout.container.md}px;
    }
    ${up('xl')} {
      top: 34%;
      left: 20%;
    }
  }
  &.otp-verification-modal {
    min-width: 0;
    top: 20%;
    ${up('md')} {
      width: 630px;
      top: 35%;
      left: calc(50% - 315px);
      margin-left: 0;
    }
  }

  &.login {
    ${up('md')} {
      width: 376px;
      left: 30%;
    }
  }

  &.successful-modal {
    top: 30%;
    min-width: 0;
    ${up('md')} {
      width: 439px;
      top: 35%;
      left: 30%;
    }
  }
  &.nominee-modal {
    /* max-height: 85%; */
    ${up('md')} {
      max-height: 95%;
      top: 44%;
      left: calc(50% - 360px);
      margin-left: 0;
    }
  }

  ${getWarningModalStyles}
`

const Header = styled.div`
  position: relative;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor2};
  display: flex;
  align-items: center;
  height: 64px;
  justify-content: ${({ withBackButton }) => (withBackButton ? 'flex-end' : 'space-between')};
  flex-direction: ${({ withBackButton }) => (withBackButton ? 'row-reverse' : 'row')};
  padding: ${({ withSpacingAround }) => (withSpacingAround ? `${pxToRem(20)} 0` : '0')};
  padding-left: ${({ withBackButton }) => (withBackButton ? 0 : pxToRem(16))};

  ${up('md')} {
    padding: ${({ withSpacingAround }) => (withSpacingAround ? `${pxToRem(20)} 0` : `0`)};
    padding-left: ${pxToRem(28)};
  }
  &.notification-modal {
    ${up('md')} {
      height: 72px;
    }
  }
  ${H5} {
    padding: ${({ withSpacingAround }) => (withSpacingAround ? `0` : `${pxToRem(20)} 0`)};

    ${up('md')} {
      padding: ${({ withSpacingAround }) => (withSpacingAround ? `0` : `${pxToRem(20)} 0`)};
    }
  }
`

const exitButtonStyles = css`
  /* margin-top: 28px; */
  margin-right: 12px;
  cursor: pointer;
  line-height: 0;
  z-index: 1080;
`

const Close = styled(CloseIcon)`
  ${exitButtonStyles};
`

const Back = styled(BackCaret)`
  ${exitButtonStyles};
  padding-right: 8px;
  ${up('md')} {
    padding-left: 0;
  }
`

const Body = styled.div`
  flex-grow: 1;
  ${basePadding};
  overflow-x: hidden;
  overflow-y: auto;

  scrollbar-color: ${props => props.theme.palette.grey4} ${props => props.theme.palette.white};
  scrollbar-width: thin;
  ${up('md')} {
    scrollbar-width: auto;
  }
  /* ::-webkit-scrollbar {
    width: 18px;
    ${up('md')} {
      width: 34px;
    }
  }
  ::-webkit-scrollbar-thumb {
    border-top: -0.88em;
    border: 7.5px solid ${props => props.theme.palette.white};
    border-radius: 12px;
    background-clip: padding-box;
    background-color: ${props => props.theme.palette.grey4};
    ${up('md')} {
      border-top: -0.88em;
      border-radius: 18px;
      border: 0.88em solid ${props => props.theme.palette.white};
    }
  }

  ::-webkit-scrollbar-corner {
    background-color: transparent;
  } */
  &.nominee-modal-body {
    height: auto;
  }
`

const Footer = styled.div`
  ${basePadding};
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${up('md')} {
    padding: ${pxToRem(20)};
  }
`
